import React, { useState, useImperativeHandle, forwardRef, useEffect, useMemo, useRef } from 'react';
import { Text } from "@constants/Themed";
import { Pressable, Platform, View, ScrollView } from 'react-native';
import { AntDesign, Feather, MaterialIcons, FontAwesome } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import { XStack, YStack, Dialog } from 'tamagui';
import departSelectList from '@constants/departSelectList';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { useQueryClient } from '@tanstack/react-query';
import * as Animatable from 'react-native-animatable';

interface EditUserModalProps {
  companyId?: string;
  isUserView?: boolean; // true if user view (connectedUsers), false if company view
  departmentList?: Array<{ _id: string; department_name: string }>; // For company view - departments with IDs
}

export type EditUserModalHandle = {
  openModal: (user: any) => void;
  closeModal: () => void;
}

const EditUserModal = forwardRef<EditUserModalHandle, EditUserModalProps>((props, ref) => {
  const { companyId, isUserView = false, departmentList = [] } = props;
  const [visible, setVisible] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const theme = themeSelector();
  const queryClient = useQueryClient();

  // Debug: Log when visible changes
  useEffect(() => {
    console.log('🔍 EditUserModal visible state changed:', {
      visible,
      hasUser: !!user,
      userId: user?._id
    });
  }, [visible, user]);

  // Get available departments
  const availableDepartments = useMemo(() => {
    if (isUserView) {
      // User view: Use standard department list (departSelectList has {value, label} structure)
      return departSelectList.map(dept => ({
        label: String(dept.label || dept.value || ''),
        value: String(dept.value || '')
      }));
    } else {
      // Company view: Use departments from company
      if (departmentList && departmentList.length > 0) {
        return departmentList.map(dept => ({
          label: String(dept.department_name || ''),
          value: String(dept._id || '') // Use ObjectId for company view
        }));
      } else {
        // Fallback to standard list if no departments
        return departSelectList.map(dept => ({
          label: String(dept.label || dept.value || ''),
          value: String(dept.value || '')
        }));
      }
    }
  }, [isUserView, departmentList]);

  useImperativeHandle(ref, () => ({
    openModal: (userData: any) => {
      console.log('📝 Opening EditUserModal for user:', {
        userData,
        userId: userData?._id,
        email: userData?.email,
        department: userData?.department || userData?.profile_information?.department
      });
      setUser(userData);
      // Extract department - could be in user.department or user.profile_information.department
      // Ensure it's always a string, not an object
      let dept = userData.department || 
                 userData.profile_information?.department || 
                 '';
      
      // If department is an object, extract the value
      if (typeof dept === 'object' && dept !== null) {
        dept = dept.value || dept._id || dept.department_name || dept.toString() || '';
      }
      
      // Ensure it's a string
      dept = String(dept || '');
      
      setSelectedDepartment(dept);
      console.log('📝 Setting visible to true, selectedDepartment:', dept);
      setVisible(true);
    },
    closeModal: () => {
      setVisible(false);
      setUser(null);
      setSelectedDepartment('');
    }
  }));

  const handleSave = async () => {
    if (!user || !user._id) {
      console.error('❌ No user selected');
      return;
    }

    setLoading(true);
    try {
      if (isUserView) {
        // User view: Update the user's profile_information.department directly
        console.log('📝 Updating user department (user view):', {
          targetUserId: user._id,
          department: selectedDepartment
        });

        const response = await apis.updateUserDepartment(user._id, selectedDepartment);

        if (response?.success) {
          console.log('✅ User department updated successfully');
          // Refresh the hierarchy
          queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });
          setVisible(false);
        } else {
          console.error('❌ Failed to update user department');
        }
      } else {
        // Company view: Use assignUserToDepartment API
        if (!companyId) {
          console.error('❌ Company ID required for company view');
          setLoading(false);
          return;
        }

        console.log('📝 Assigning user to department (company view):', {
          companyId,
          userId: user._id,
          department: selectedDepartment
        });

        // For company view, we need to find the department ObjectId from departmentList
        // If selectedDepartment is a name, find the matching department _id
        let departmentId = selectedDepartment;
        
        if (departmentList && departmentList.length > 0) {
          // Check if selectedDepartment is already an ObjectId
          const isObjectId = /^[0-9a-fA-F]{24}$/.test(selectedDepartment);
          
          if (!isObjectId) {
            // Find department by name
            const foundDept = departmentList.find(
              dept => dept.department_name === selectedDepartment
            );
            if (foundDept) {
              departmentId = foundDept._id;
            } else {
              console.warn('⚠️ Department not found in departmentList, using name as-is');
            }
          }
        }

        const response = await apis.assignUserToDepartment(companyId, {
          user: user._id,
          departmentId: departmentId
        });

        if (response?.success) {
          console.log('✅ User assigned to department successfully');
          queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });
          setVisible(false);
        } else {
          console.error('❌ Failed to assign user to department');
        }
      }
    } catch (error) {
      console.error('❌ Error updating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const userName = user?.profile_information?.name && user?.profile_information?.surname
    ? `${user.profile_information.name} ${user.profile_information.surname}`
    : user?.profile_information?.name || user?.email || 'Unknown User';

  const currentDepartment = user?.department || user?.profile_information?.department || 'Unassigned';

  console.log('🎬 EditUserModal render:', {
    visible,
    hasUser: !!user,
    userId: user?._id,
    selectedDepartment,
    availableDepartmentsCount: availableDepartments.length
  });

  if (!visible || !user) {
    console.log('⏸️ EditUserModal not rendering - visible:', visible, 'hasUser:', !!user);
    return null;
  }

  return (
    <Dialog open={visible} onOpenChange={setVisible}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 500,
              maxWidth: 600,
              maxHeight: "85%",
              borderRadius: 24,
              overflow: 'visible', // Changed from 'hidden' to allow dropdown to show
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              zIndex: 2000, // Ensure Dialog.Content has high z-index
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3" justifyContent="space-between">
              <XStack alignItems="center" gap="$3" flex={1}>
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <Feather
                    name="user"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    Edit User
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    Modify user properties and department assignment
                  </Dialog.Description>
                </YStack>
              </XStack>
              <Dialog.Close asChild>
                <Pressable onPress={() => setVisible(false)}>
                  <AntDesign
                    name="close"
                    size={20}
                    color={theme ? '#64748b' : '#94a3b8'}
                  />
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>

          {/* Content */}
          <View style={{ padding: 24, overflow: 'visible', position: 'relative', zIndex: 10 }}>
            <YStack gap="$4" style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}>
              {/* User Info Card */}
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <XStack alignItems="center" gap="$3">
                  <AvatarHandler
                    avatar={user.profile_information?.avatar?.img}
                    avatarImageStyle={{ width: 60, height: 60, borderRadius: 30 }}
                    fallbackInitials={userName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                    fallbackStyle={{
                      width: 60,
                      height: 60,
                      borderRadius: 30,
                      backgroundColor: theme ? '#e2e8f0' : '#4a5568',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    fallbackTextStyle={{
                      color: theme ? '#2d3748' : '#ffffff',
                      fontSize: 20,
                      fontWeight: 'bold'
                    }}
                  />
                  <YStack flex={1}>
                    <Text style={{ 
                      fontSize: 18, 
                      fontWeight: '700',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      marginBottom: 4
                    }}>
                      {userName}
                    </Text>
                    {user.email && (
                      <Text style={{ 
                        fontSize: 14,
                        color: theme ? '#64748b' : '#94a3b8',
                        marginBottom: 4
                      }}>
                        {user.email}
                      </Text>
                    )}
                    <Text style={{ 
                      fontSize: 12,
                      color: theme ? '#94a3b8' : '#64748b',
                      fontWeight: '500'
                    }}>
                      Current Department: {currentDepartment}
                    </Text>
                  </YStack>
                </XStack>
              </View>

              {/* Department Selection */}
              <YStack gap="$2" style={{ position: 'relative', zIndex: showDepartmentDropdown ? 100 : 1 }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  Department
                </Text>
                <View style={{ 
                  position: 'relative', 
                  zIndex: showDepartmentDropdown ? 100 : 1,
                }}>
                  <Pressable
                    onPress={() => setShowDepartmentDropdown(!showDepartmentDropdown)}
                    style={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderWidth: 1,
                      borderColor: showDepartmentDropdown ? '#3b82f6' : (theme ? '#e2e8f0' : '#374151'),
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 14,
                      minHeight: 50,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 3,
                    }}
                  >
                    <Text style={{
                      color: selectedDepartment ? (theme ? '#1e293b' : '#f8fafc') : (theme ? '#64748b' : '#94a3b8'),
                      fontSize: 16,
                      fontWeight: selectedDepartment ? '600' : '400',
                      flex: 1
                    }}>
                      {selectedDepartment 
                        ? (availableDepartments.find(d => d.value === selectedDepartment)?.label || selectedDepartment)
                        : "Select department..."}
                    </Text>

                    <Animatable.View
                      animation={showDepartmentDropdown ? 'rotate' : undefined}
                      duration={200}
                    >
                      <FontAwesome
                        name={showDepartmentDropdown ? 'chevron-up' : 'chevron-down'}
                        size={14}
                        color={theme ? '#64748b' : '#94a3b8'}
                      />
                    </Animatable.View>
                  </Pressable>

                  {/* Dropdown Options */}
                  {showDepartmentDropdown && (
                    <Animatable.View
                      animation="fadeInDown"
                      duration={200}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: theme ? 'white' : '#1e293b',
                        borderWidth: 1,
                        borderColor: theme ? '#e2e8f0' : '#374151',
                        borderRadius: 12,
                        marginTop: 4,
                        maxHeight: 300,
                        zIndex: 200, // Higher than content (10) and footer (1)
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 8 },
                        shadowOpacity: 0.15,
                        shadowRadius: 12,
                        elevation: 200, // Higher than content and footer
                      }}
                    >
                      <ScrollView
                        style={{ maxHeight: 300 }}
                        showsVerticalScrollIndicator={false}
                        nestedScrollEnabled
                      >
                        {availableDepartments.map((dept, index) => {
                          const itemValue = String(dept.value || '');
                          const itemLabel = String(dept.label || '');
                          const isSelected = selectedDepartment === itemValue;
                          
                          return (
                            <Pressable
                              key={itemValue || index}
                              onPress={() => {
                                console.log('📝 Department changed to:', itemValue);
                                setSelectedDepartment(itemValue);
                                setShowDepartmentDropdown(false);
                              }}
                              style={({ pressed }) => ({
                                paddingHorizontal: 16,
                                paddingVertical: 14,
                                backgroundColor: pressed
                                  ? (theme ? '#f1f5f9' : '#334155')
                                  : (isSelected
                                    ? (theme ? '#e0f2fe' : '#1e40af20')
                                    : 'transparent'),
                                borderBottomWidth: index < availableDepartments.length - 1 ? 1 : 0,
                                borderBottomColor: theme ? '#f1f5f9' : '#334155',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              })}
                            >
                              <Text style={{
                                color: isSelected
                                  ? (theme ? '#0369a1' : '#60a5fa')
                                  : (theme ? '#1e293b' : '#f8fafc'),
                                fontSize: 16,
                                fontWeight: isSelected ? '600' : '400',
                                flex: 1
                              }}>
                                {itemLabel}
                              </Text>

                              {isSelected && (
                                <FontAwesome
                                  name="check"
                                  size={14}
                                  color={theme ? '#0369a1' : '#60a5fa'}
                                />
                              )}
                            </Pressable>
                          );
                        })}
                      </ScrollView>
                    </Animatable.View>
                  )}

                  {/* Backdrop to close dropdown */}
                  {showDepartmentDropdown && (
                    <Pressable
                      onPress={() => setShowDepartmentDropdown(false)}
                      style={{
                        position: 'absolute',
                        top: -1000,
                        left: -1000,
                        right: -1000,
                        bottom: -1000,
                        zIndex: 199, // Just below dropdown (200)
                        backgroundColor: 'transparent',
                      }}
                    />
                  )}
                </View>
              </YStack>
            </YStack>
          </View>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
            zIndex: 1, // Lower than dropdown
            position: 'relative',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={() => setVisible(false)}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Cancel
                  </Text>
                </Pressable>
              </Dialog.Close>
              <Dialog.Close asChild>
                <Pressable
                  onPress={handleSave}
                  disabled={loading}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: loading ? (theme ? '#9ca3af' : '#6b7280') : (theme ? '#3b82f6' : '#2563eb'),
                    minWidth: 100,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: '#3b82f6',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    {loading ? "Saving..." : "Save Changes"}
                  </Text>
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
});

EditUserModal.displayName = 'EditUserModal';

export default EditUserModal;

